/**
 * Email Service for Booking Notifications
 * Handles sending confirmation emails and admin notifications
 */

import { supabase } from './customSupabaseClient';

class EmailService {
  constructor() {
    this.isDevelopment = import.meta.env.DEV || 
      (typeof window !== 'undefined' && 
       (window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname.includes('localhost')));
  }

  /**
   * Send booking confirmation email to customer
   * @param {Object} bookingData - Booking data
   * @param {string} customerEmail - Customer email address
   * @returns {Promise<boolean>} Success status
   */
  async sendBookingConfirmation(bookingData, customerEmail) {
    try {
      // Prepare email data for Supabase Edge Function (matching your function's expected structure)
      const emailData = {
        record: {
          email: customerEmail,
          name: bookingData.customer_name,
          phone: bookingData.customer_phone,
          trip_type: bookingData.trip_type,
          datetime: bookingData.pickup_time,
          vehicle: bookingData.vehicle_type,
          passengers: bookingData.passengers || 1,
          pickup: bookingData.pickup_address,
          dropoff: bookingData.dropoff_address,
          hours: bookingData.hours,
          estimate_amount: bookingData.price,
          estimate_distance: bookingData.distance_miles,
          instructions: bookingData.special_requests || 'None'
        }
      };

      console.log('📧 Attempting to send customer confirmation email to:', customerEmail);
      console.log('📧 Email data:', emailData);

      // Try to use Supabase Edge Function (fixed version)
      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: emailData
      });

      if (error) {
        console.error('❌ Supabase email function error:', error);
        console.error('❌ Full error object:', JSON.stringify(error, null, 2));
        console.error('❌ Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          status: error.status,
          statusText: error.statusText,
          context: error.context,
          name: error.name,
          stack: error.stack
        });
        return false;
      }

      console.log('📧 Email function response:', data);
      
      if (data && (data.success || data.message === 'Emails sent successfully')) {
        console.log('✅ Customer confirmation email sent successfully:', data);
        return true;
      } else {
        console.error('❌ Email function returned unsuccessful response:', data);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending booking confirmation email:', error);
      return false;
    }
  }

  /**
   * Send admin notification email
   * @param {Object} bookingData - Booking data
   * @returns {Promise<boolean>} Success status
   */
  async sendAdminNotification(bookingData) {
    try {
      // Prepare admin email data (matching your function's expected structure)
      const adminEmailData = {
        record: {
          email: bookingData.customer_email,
          name: bookingData.customer_name,
          phone: bookingData.customer_phone,
          trip_type: bookingData.trip_type,
          datetime: bookingData.pickup_time,
          vehicle: bookingData.vehicle_type,
          passengers: bookingData.passengers || 1,
          pickup: bookingData.pickup_address,
          dropoff: bookingData.dropoff_address,
          hours: bookingData.hours,
          estimate_amount: bookingData.price,
          estimate_distance: bookingData.distance_miles,
          instructions: bookingData.special_requests || 'None'
        }
      };

      console.log('📧 Attempting to send admin notification email');
      console.log('📧 Admin email data:', adminEmailData);

      // Try to use Supabase Edge Function (fixed version)
      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: adminEmailData
      });

      if (error) {
        console.error('❌ Supabase admin email function error:', error);
        console.error('❌ Full admin error object:', JSON.stringify(error, null, 2));
        console.error('❌ Admin error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          status: error.status,
          statusText: error.statusText,
          context: error.context,
          name: error.name,
          stack: error.stack
        });
        return false;
      }

      console.log('📧 Admin email function response:', data);
      
      if (data && (data.success || data.message === 'Emails sent successfully')) {
        console.log('✅ Admin notification email sent successfully:', data);
        return true;
      } else {
        console.error('❌ Admin email function returned unsuccessful response:', data);
        return false;
      }
    } catch (error) {
      console.error('❌ Error sending admin notification email:', error);
      return false;
    }
  }

  /**
   * Send both customer confirmation and admin notification
   * @param {Object} bookingData - Booking data
   * @param {string} customerEmail - Customer email address
   * @param {number} passengers - Number of passengers (from form state)
   * @returns {Promise<Object>} Results of both email sends
   */
  async sendBookingEmails(bookingData, customerEmail, passengers = 1) {
    try {
      // Add passengers to booking data for email sending
      const emailBookingData = {
        ...bookingData,
        passengers: passengers
      };

      // Call the Edge Function ONCE - it sends both customer and admin emails
      console.log('📧 Sending both customer and admin emails in one call...');
      
      const emailData = {
        record: {
          email: customerEmail,
          name: emailBookingData.customer_name,
          phone: emailBookingData.customer_phone,
          trip_type: emailBookingData.trip_type,
          datetime: emailBookingData.pickup_time,
          vehicle: emailBookingData.vehicle_type,
          passengers: passengers,
          pickup: emailBookingData.pickup_address,
          dropoff: emailBookingData.dropoff_address,
          hours: emailBookingData.hours,
          estimate_amount: emailBookingData.price,
          estimate_distance: emailBookingData.distance_miles,
          instructions: emailBookingData.special_requests || 'None'
        }
      };

      console.log('📧 Email data being sent:', emailData);

      const { data, error } = await supabase.functions.invoke('send-booking-email', {
        body: emailData
      });

      if (error) {
        console.error('❌ Supabase email function error:', error);
        return {
          customerEmailSent: false,
          adminEmailSent: false,
          success: false
        };
      }

      console.log('📧 Email function response:', data);
      
      // The Edge Function sends both emails, so both are successful if the function succeeds
      return {
        customerEmailSent: true,
        adminEmailSent: true,
        success: true
      };
    } catch (error) {
      console.error('Error sending booking emails:', error);
      return {
        customerEmailSent: false,
        adminEmailSent: false,
        success: false
      };
    }
  }

  /**
   * Generate email template for booking confirmation
   * @param {Object} bookingData - Booking data
   * @returns {string} HTML email template
   */
  generateBookingConfirmationTemplate(bookingData) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Booking Confirmation - Akib Black Car Service</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; background: #fff; }
          .header { background: #1a1a1a; color: #D4AF37; padding: 30px; text-align: center; }
          .content { padding: 30px; background: #f8f9fa; }
          .booking-details { background: #e9ecef; padding: 20px; border-radius: 5px; margin: 20px 0; }
          .footer { background: #1a1a1a; color: #fff; padding: 20px; text-align: center; font-size: 14px; }
          .highlight { color: #D4AF37; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Booking Confirmation</h1>
            <p>Akib Black Car Service</p>
          </div>
          <div class="content">
            <p>Dear ${bookingData.customer_name},</p>
            <p>Thank you for choosing Akib Black Car Service! Your booking has been confirmed.</p>
            
            <div class="booking-details">
              <h3>Booking Details</h3>
              <p><strong>Booking ID:</strong> ${bookingData.id}</p>
              <p><strong>Date & Time:</strong> ${bookingData.pickup_time}</p>
              <p><strong>Vehicle:</strong> ${bookingData.vehicle_type}</p>
              <p><strong>Pickup:</strong> ${bookingData.pickup_address}</p>
              <p><strong>Dropoff:</strong> ${bookingData.dropoff_address}</p>
              <p><strong>Trip Type:</strong> ${bookingData.trip_type}</p>
              <p><strong>Estimated Price:</strong> $${bookingData.price}</p>
              ${bookingData.special_requests ? `<p><strong>Special Requests:</strong> ${bookingData.special_requests}</p>` : ''}
            </div>
            
            <p>Your driver will contact you 15 minutes before pickup time. If you have any questions or need to make changes, please contact us immediately.</p>
            <p>We look forward to providing you with excellent service!</p>
            <p>Best regards,<br>Akib Black Car Service Team</p>
          </div>
          <div class="footer">
            <p>© 2025 Akib Black Car Service. All rights reserved.</p>
            <p>For support, call us at (555) 123-4567 or email support@akibblackcar.com</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate email template for admin notification
   * @param {Object} bookingData - Booking data
   * @returns {string} HTML email template
   */
  generateAdminNotificationTemplate(bookingData) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Booking Request - Akib Black Car Service</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
          .container { max-width: 600px; margin: 0 auto; background: #fff; }
          .header { background: #dc3545; color: #fff; padding: 30px; text-align: center; }
          .content { padding: 30px; background: #f8f9fa; }
          .booking-details { background: #e9ecef; padding: 20px; border-radius: 5px; margin: 20px 0; }
          .footer { background: #1a1a1a; color: #fff; padding: 20px; text-align: center; font-size: 14px; }
          .urgent { color: #dc3545; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>New Booking Request</h1>
            <p>Akib Black Car Service - Admin Panel</p>
          </div>
          <div class="content">
            <p class="urgent">A new booking has been submitted and requires your attention.</p>
            
            <div class="booking-details">
              <h3>Customer Information</h3>
              <p><strong>Name:</strong> ${bookingData.customer_name}</p>
              <p><strong>Email:</strong> ${bookingData.customer_email}</p>
              <p><strong>Phone:</strong> ${bookingData.customer_phone}</p>
              
              <h3>Booking Details</h3>
              <p><strong>Booking ID:</strong> ${bookingData.id}</p>
              <p><strong>Date & Time:</strong> ${bookingData.pickup_time}</p>
              <p><strong>Vehicle:</strong> ${bookingData.vehicle_type}</p>
              <p><strong>Pickup:</strong> ${bookingData.pickup_address}</p>
              <p><strong>Dropoff:</strong> ${bookingData.dropoff_address}</p>
              <p><strong>Trip Type:</strong> ${bookingData.trip_type}</p>
              <p><strong>Estimated Price:</strong> $${bookingData.price}</p>
              ${bookingData.special_requests ? `<p><strong>Special Requests:</strong> ${bookingData.special_requests}</p>` : ''}
              <p><strong>Submitted:</strong> ${bookingData.created_at}</p>
            </div>
            
            <p>Please review this booking and contact the customer to confirm details.</p>
            <p>Login to the admin panel to manage this booking.</p>
          </div>
          <div class="footer">
            <p>© 2025 Akib Black Car Service. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
}

// Create singleton instance
const emailService = new EmailService();

export { emailService };
export default emailService;
